import React, { useState, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Tab,
  Tabs,
  Box,
  CircularProgress,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useVideoStream } from "../../providers/videoStreams";
import { ModalContainer } from "./styles";
import { FiUpload, FiLink, FiAlertCircle } from "react-icons/fi";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`video-upload-tabpanel-${index}`}
      aria-labelledby={`video-upload-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

interface VideoUploadModalProps {
  open: boolean;
  onClose: () => void;
}

const VideoUploadModal: React.FC<VideoUploadModalProps> = ({
  open,
  onClose,
}) => {
  const { t } = useTranslation();
  const { getUploadUrl, uploadVideoFile, getAllVideoStreams } =
    useVideoStream();
  const [tabValue, setTabValue] = useState(0);
  const [videoName, setVideoName] = useState("");
  const [videoDescription, setVideoDescription] = useState("");
  const [videoUrl, setVideoUrl] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    // Reset form when switching tabs
    setError("");
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      setSelectedFile(files[0]);
      if (!videoName) {
        // Use filename as default name (without extension)
        const fileName = files[0].name.replace(/\.[^/.]+$/, "");
        setVideoName(fileName);
      }
    }
  };

  const handleBrowseClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const validateForm = () => {
    if (!videoName.trim()) {
      setError(t("video_name_required"));
      return false;
    }

    if (tabValue === 0 && !selectedFile) {
      setError(t("video_file_required"));
      return false;
    }

    if (tabValue === 1 && !videoUrl.trim()) {
      setError(t("video_url_required"));
      return false;
    }

    return true;
  };

  const handleDirectUpload = async () => {
    if (!validateForm() || !selectedFile) return;

    setIsUploading(true);
    setUploadProgress(0);
    setError("");

    try {
      // 1. Get upload URL from backend
      const uploadData = await getUploadUrl({
        name: videoName,
        description: videoDescription,
      });

      if (!uploadData) {
        throw new Error(t("failed_to_get_upload_url"));
      }

      // 2. Upload the file directly to Cloudflare
      const uploadResult = await uploadVideoFile(
        uploadData.uploadURL,
        selectedFile
      );

      if (!uploadResult) {
        throw new Error(t("upload_failed"));
      }

      // 3. Success - close modal and refresh list
      getAllVideoStreams();
      onClose();
    } catch (err: any) {
      setError(err.message || t("upload_error"));
    } finally {
      setIsUploading(false);
    }
  };

  const handleLinkUpload = async () => {
    if (!validateForm()) return;

    setIsUploading(true);
    setError("");

    try {
      // Request backend to create video from URL
      const uploadData = await getUploadUrl({
        name: videoName,
        description: videoDescription,
      });

      if (!uploadData) {
        throw new Error(t("failed_to_process_video_url"));
      }

      // Success - close modal and refresh list
      getAllVideoStreams();
      onClose();
    } catch (err: any) {
      setError(err.message || t("upload_error"));
    } finally {
      setIsUploading(false);
    }
  };

  const handleSubmit = () => {
    if (tabValue === 0) {
      handleDirectUpload();
    } else {
      handleLinkUpload();
    }
  };

  const handleClose = () => {
    if (!isUploading) {
      setVideoName("");
      setVideoDescription("");
      setVideoUrl("");
      setSelectedFile(null);
      setError("");
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <ModalContainer>
        <DialogTitle>{t("upload_video")}</DialogTitle>

        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="fullWidth"
          aria-label="video upload methods"
        >
          <Tab
            icon={<FiUpload />}
            label={t("quick_upload")}
            iconPosition="start"
          />
          <Tab icon={<FiLink />} label={t("use_link")} iconPosition="start" />
        </Tabs>

        <DialogContent>
          {error && (
            <Box className="errorMessage">
              <FiAlertCircle />
              <Typography>{error}</Typography>
            </Box>
          )}

          <TextField
            label={t("video_name")}
            fullWidth
            margin="normal"
            value={videoName}
            onChange={(e) => setVideoName(e.target.value)}
            required
          />

          <TextField
            label={t("video_description")}
            fullWidth
            margin="normal"
            multiline
            rows={3}
            value={videoDescription}
            onChange={(e) => setVideoDescription(e.target.value)}
          />

          <TabPanel value={tabValue} index={0}>
            <input
              type="file"
              accept="video/*"
              style={{ display: "none" }}
              ref={fileInputRef}
              onChange={handleFileSelect}
            />

            <div className="uploadArea" onClick={handleBrowseClick}>
              {selectedFile ? (
                <div className="selectedFile">
                  <Typography variant="body1">{selectedFile.name}</Typography>
                  <Typography variant="body2">
                    {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB
                  </Typography>
                </div>
              ) : (
                <>
                  <FiUpload size={36} />
                  <Typography variant="body1">
                    {t("drag_drop_or_click")}
                  </Typography>
                  <Typography variant="body2">{t("max_file_size")}</Typography>
                </>
              )}
            </div>
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <TextField
              label={t("video_url")}
              placeholder="https://example.com/video.mp4"
              fullWidth
              margin="normal"
              value={videoUrl}
              onChange={(e) => setVideoUrl(e.target.value)}
              required
            />
            <Typography variant="body2" className="helpText">
              {t("link_upload_help")}
            </Typography>
          </TabPanel>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} disabled={isUploading}>
            {t("cancel")}
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={isUploading}
            startIcon={isUploading ? <CircularProgress size={16} /> : null}
          >
            {isUploading ? t("uploading") : t("upload")}
          </Button>
        </DialogActions>
      </ModalContainer>
    </Dialog>
  );
};

export default VideoUploadModal;
